import { DateTime } from "luxon";
import { fetchAccessToken, fetchPingSLO } from "../api/auth";
import { NO_APP_ACCESS_WARNING_MESSAGE } from "./messageConstants";
import { clearAllReduxState } from "./commonUtils";
import { disconnect } from "./webSocketClient";

export const login = async (authCode) => {
  try {
    let accessTokenObject = await getAccessTokenObject(authCode);

    saveAccessTokenToCookie(accessTokenObject);

    return accessTokenObject;
  } catch (e) {
    console.error(e.message);
    return { error: e.message };
  }
};

export const logout = async (refreshToken) => {
  try {
    await fetchPingSLO();
    disconnect();
  } catch (e) {
    console.error("Error during logout!", e);
  } finally {
    deleteAccessTokenCookie();
    window.location.href = process.env.REACT_APP_PING_REDIRECT_URL + "#/logout";
    clearAllReduxState();
  }
};

export const getAccessTokenFromCookie = () => {
  let activeSessionCookie = getSessionCookieData();
  if (activeSessionCookie) {
    return activeSessionCookie.access_token;
  }
  return null;
};

export const getIdTokenFromCookie = () => {
  let activeSessionCookie = getSessionCookieData();
  if (activeSessionCookie) {
    return activeSessionCookie.id_token;
  }
  return null;
};

export const getEidFromIdTokenInCookie = () => {
  const idToken = { id_token: getIdTokenFromCookie() };
  const decodedIdToken = parseJwt(idToken);
  return decodedIdToken?.sub;
};

export const getExpiresInTimeFromCookie = () => {
  let activeSessionCookie = getSessionCookieData();
  if (activeSessionCookie) {
    return activeSessionCookie.expires_in;
  }
  return null;
};

export const isSessionCookieActive = () => {
  const cookie = RegExp(process.env.REACT_APP_PING_COOKIE_NAME + "=[^;]+").exec(document.cookie);
  if (cookie !== null) {
    return cookie.length > 0;
  }
  return false;
};

export const getTimeUntilCookieExpires = (expiresIn) => {
  const cookieExpiresInISOString = expiresIn;
  if (cookieExpiresInISOString !== null && cookieExpiresInISOString !== undefined) {
    let cookieExpiresInDateTime = DateTime.fromISO(cookieExpiresInISOString).toUTC().startOf("second");
    let nowInUTCDateTime = DateTime.now().toUTC().startOf("second");
    let difference = cookieExpiresInDateTime.diff(nowInUTCDateTime, ["hours", "minutes", "seconds"]).toObject();
    difference.seconds = Math.floor(difference.seconds);
    return difference;
  }
  return null;
};

export const isSearchParamsInUrl = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  // eslint-disable-next-line no-unused-vars
  for (let key of urlSearchParams.keys()) {
    return true;
  }
  return false;
};

export const isLogout = () => {
  return window.location.href.includes("logout");
};

export const goToPingLogin = () => {
  let URL = process.env.REACT_APP_PING_AUTH_URL;
  let responseType = "code";
  let clientId = "STARS";
  let redirectUri = process.env.REACT_APP_PING_REDIRECT_URL;
  let path = window.location.hash.slice(1);
  if (path !== "/") {
    sessionStorage.setItem('originalURI', path);
  }
  let scope = "openid profile";
  window.location.href =
    URL +
    "?response_type=" +
    responseType +
    "&scope=" +
    scope +
    "&client_id=" +
    clientId +
    "&redirect_uri=" +
    redirectUri;
};

export const isQueryStringKeyInUrl = (queryStringKey) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return urlSearchParams.has(queryStringKey);
};

export const getQueryStringKeyInUrl = (queryStringKey) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (isQueryStringKeyInUrl(queryStringKey)) {
    return urlSearchParams.get(queryStringKey);
  }
  return null;
};

const getAccessTokenObject = async (authCode) => {
  let accessTokenResponse = await fetchAccessToken(authCode).then((res) => res.json());

  if (accessTokenResponse.message && accessTokenResponse.message === NO_APP_ACCESS_WARNING_MESSAGE) {
    deleteAccessTokenCookie();
    window.localStorage.clear();
    throw new Error(NO_APP_ACCESS_WARNING_MESSAGE);
  }

  if (!accessTokenResponse.access_token) {
    deleteAccessTokenCookie();
    window.localStorage.clear();
    throw new Error("Access token call failed! Access Token field missing.");
  }

  return accessTokenResponse;
};

const saveAccessTokenToCookie = (accessTokenObject) => {
  let cookieName = process.env.REACT_APP_PING_COOKIE_NAME;
  let accessTokenExpireTimeInMillis = accessTokenObject.expires_in * 1000;
  let date = new Date();
  date.setTime(date.getTime() + accessTokenExpireTimeInMillis);
  let cookieValue = JSON.stringify({
    access_token: accessTokenObject.access_token,
    id_token: accessTokenObject.id_token,
    expires_in: date.toISOString(),
  });
  document.cookie = cookieName + "=" + cookieValue + ";expires=" + date.toUTCString() + ";path=/";
};

export const deleteAccessTokenCookie = () => {
  let cookieName = process.env.REACT_APP_PING_COOKIE_NAME;
  document.cookie = cookieName + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;";
};

export const getSessionCookieData = () => {
  if (isSessionCookieActive()) {
    let sessionCookieString = RegExp(process.env.REACT_APP_PING_COOKIE_NAME + "=[^;]+").exec(document.cookie)[0];
    let sessionCookieValueString = sessionCookieString.split("=")[1];
    let cookieValue = JSON.parse(sessionCookieValueString);
    return cookieValue;
  }
  // No session cookie, clear user data and go back to ping
  window.localStorage.clear();
  goToPingLogin();
  return null;
  // throw Error("Tried to get session cookie data when no session cookie was active.");
};

// NOT USED AT THIS TIME
// const revokeAccessToken = async (refreshToken) => {
//   let requestParams = {
//     method: "POST",
//     timeout: 0,
//     headers: {
//       "Content-Type": "application/json",
//     },
//     body: JSON.stringify({
//       token: refreshToken,
//     }),
//   };
//   let revokeAccessTokenResponse = await fetch(process.env.REACT_APP_AUTH_API + "revoke", requestParams).then((res) =>
//     res.json()
//   );
//   return revokeAccessTokenResponse;
// };

const parseJwt = (tokenObject) => {
  let token = tokenObject.id_token;
  let base64Url = token.split(".")[1];
  let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  let jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

export const removeAuthCodeFromQueryString = () => {
  const newUrl = new URL(window.location.href);
  newUrl.searchParams.delete("code");
  window.history.replaceState({}, document.title, newUrl.toString());
};
